import {
  FILTER_OPS_BY_TYPE,
  type FieldType,
  type FilterNode,
  type FilterOp,
  isMultiValued,
  isSortable,
  ME_TOKEN,
  type SearchRequest,
  type SystemField,
} from '@servicetim/shared';

/** What the validator needs to know about a custom field. */
export interface RegistryField {
  key: string;
  type: FieldType;
  archived: boolean;
}

/** Custom fields visible to the query, keyed by `cf_` key. */
export type QueryRegistry = ReadonlyMap<string, RegistryField>;

export type QueryIssueCode =
  | 'query.unknown_field'
  | 'query.archived_field'
  | 'query.op_not_allowed'
  | 'query.bad_value'
  | 'query.me_on_non_user_field'
  | 'query.unsortable_field'
  | 'query.ungroupable_field'
  | 'query.too_deep';

export interface QueryIssue {
  /** Path inside the request, e.g. `filter.and[1].op`. */
  path: string;
  code: QueryIssueCode;
  message: string;
}

/** Filter semantics of every system field, expressed as the closest custom field type. */
export const SYSTEM_FIELD_TYPES: Record<SystemField, FieldType | 'text_search'> = {
  id: 'ticket_lookup',
  key: 'text',
  number: 'number',
  project_id: 'single_select',
  issue_type_id: 'single_select',
  status_id: 'single_select',
  'status.category': 'single_select',
  priority_id: 'single_select',
  'priority.rank': 'number',
  summary: 'text',
  description: 'long_text',
  reporter_id: 'user',
  assignee_id: 'user',
  created_by: 'user',
  organization_id: 'single_select',
  labels: 'tags',
  watchers: 'multi_user',
  parent_id: 'ticket_lookup',
  created_at: 'datetime',
  updated_at: 'datetime',
  resolved_at: 'datetime',
  due_at: 'datetime',
  acknowledged_at: 'datetime',
  acknowledged_by: 'user',
  text: 'text_search',
};

export const MAX_FILTER_DEPTH = 6;

const ARRAY_OPS: ReadonlySet<FilterOp> = new Set(['in', 'not_in', 'any_of', 'all_of', 'none_of']);
const NO_VALUE_OPS: ReadonlySet<FilterOp> = new Set(['is_null', 'not_null']);

type Resolved =
  | { kind: 'ok'; type: FieldType | 'text_search'; archived: boolean }
  | { kind: 'unknown' };

function resolveField(field: string, registry: QueryRegistry): Resolved {
  if (field in SYSTEM_FIELD_TYPES) {
    return { kind: 'ok', type: SYSTEM_FIELD_TYPES[field as SystemField], archived: false };
  }
  const custom = registry.get(field);
  return custom
    ? { kind: 'ok', type: custom.type, archived: custom.archived }
    : { kind: 'unknown' };
}

function containsMe(value: unknown): boolean {
  if (value === ME_TOKEN) {
    return true;
  }
  return Array.isArray(value) && value.some((v) => v === ME_TOKEN);
}

function checkField(
  field: string,
  path: string,
  registry: QueryRegistry,
  issues: QueryIssue[],
): Resolved {
  const resolved = resolveField(field, registry);
  if (resolved.kind === 'unknown') {
    issues.push({ path, code: 'query.unknown_field', message: `unknown field "${field}"` });
  } else if (resolved.archived) {
    issues.push({ path, code: 'query.archived_field', message: `field "${field}" is archived` });
  }
  return resolved;
}

function validateCondition(
  node: { field: string; op: FilterOp; value?: unknown },
  path: string,
  registry: QueryRegistry,
  issues: QueryIssue[],
): void {
  const resolved = checkField(node.field, `${path}.field`, registry, issues);
  if (resolved.kind !== 'ok') {
    return;
  }
  const allowed: readonly FilterOp[] =
    resolved.type === 'text_search' ? ['search'] : FILTER_OPS_BY_TYPE[resolved.type];
  if (!allowed.includes(node.op)) {
    issues.push({
      path: `${path}.op`,
      code: 'query.op_not_allowed',
      message: `op "${node.op}" is not allowed on "${node.field}"`,
    });
    return;
  }
  const valuePath = `${path}.value`;
  if (NO_VALUE_OPS.has(node.op)) {
    if (node.value !== undefined) {
      issues.push({
        path: valuePath,
        code: 'query.bad_value',
        message: `"${node.op}" takes no value`,
      });
    }
    return;
  }
  if (node.value === undefined) {
    issues.push({
      path: valuePath,
      code: 'query.bad_value',
      message: `"${node.op}" needs a value`,
    });
    return;
  }
  if (ARRAY_OPS.has(node.op) && !Array.isArray(node.value)) {
    issues.push({
      path: valuePath,
      code: 'query.bad_value',
      message: `"${node.op}" needs an array`,
    });
  }
  if (node.op === 'between' && (!Array.isArray(node.value) || node.value.length !== 2)) {
    issues.push({
      path: valuePath,
      code: 'query.bad_value',
      message: '"between" needs [low, high]',
    });
  }
  if (node.op === 'search' && typeof node.value !== 'string') {
    issues.push({ path: valuePath, code: 'query.bad_value', message: '"search" needs a string' });
  }
  if (containsMe(node.value) && resolved.type !== 'user' && resolved.type !== 'multi_user') {
    issues.push({
      path: valuePath,
      code: 'query.me_on_non_user_field',
      message: `"${ME_TOKEN}" is only valid on user fields`,
    });
  }
}

function validateNode(
  node: FilterNode,
  path: string,
  depth: number,
  registry: QueryRegistry,
  issues: QueryIssue[],
): void {
  if (depth > MAX_FILTER_DEPTH) {
    issues.push({
      path,
      code: 'query.too_deep',
      message: `filter nesting exceeds ${MAX_FILTER_DEPTH}`,
    });
    return;
  }
  if ('and' in node) {
    node.and.forEach((child, i) => {
      validateNode(child, `${path}.and[${i}]`, depth + 1, registry, issues);
    });
  } else if ('or' in node) {
    node.or.forEach((child, i) => {
      validateNode(child, `${path}.or[${i}]`, depth + 1, registry, issues);
    });
  } else if ('not' in node) {
    validateNode(node.not, `${path}.not`, depth + 1, registry, issues);
  } else {
    validateCondition(node, path, registry, issues);
  }
}

/**
 * Validates a structurally valid search request against the project's field registry: known
 * and active fields, operators allowed for the field type, value shapes, `$me` only on user
 * fields, sortable sort fields and groupable group fields. Returns every issue found; an empty
 * array means the request can be compiled.
 */
export function validateSearchRequest(
  request: SearchRequest,
  registry: QueryRegistry,
): QueryIssue[] {
  const issues: QueryIssue[] = [];
  if (request.filter) {
    validateNode(request.filter, 'filter', 1, registry, issues);
  }
  request.sort.forEach((sort, i) => {
    const resolved = checkField(sort.field, `sort[${i}].field`, registry, issues);
    if (resolved.kind === 'ok' && (resolved.type === 'text_search' || !isSortable(resolved.type))) {
      issues.push({
        path: `sort[${i}].field`,
        code: 'query.unsortable_field',
        message: `cannot sort by "${sort.field}"`,
      });
    }
  });
  if (request.groupBy) {
    const resolved = checkField(request.groupBy.field, 'groupBy.field', registry, issues);
    if (
      resolved.kind === 'ok' &&
      (resolved.type === 'text_search' ||
        resolved.type === 'long_text' ||
        isMultiValued(resolved.type))
    ) {
      issues.push({
        path: 'groupBy.field',
        code: 'query.ungroupable_field',
        message: `cannot group by "${request.groupBy.field}"`,
      });
    }
  }
  request.select?.forEach((field, i) => {
    checkField(field, `select[${i}]`, registry, issues);
  });
  return issues;
}

/** Builds a registry from field rows; archived rows stay so references to them are reported precisely. */
export function buildQueryRegistry(
  fields: Iterable<{ key: string; type: FieldType; archivedAt?: Date | string | null }>,
): QueryRegistry {
  const map = new Map<string, RegistryField>();
  for (const f of fields) {
    map.set(f.key, { key: f.key, type: f.type, archived: f.archivedAt != null });
  }
  return map;
}
